import ModuleContext from 'CMFW/context/ModuleContext';
import { IRefIds } from 'constants/refs';
import { getCacheKey } from 'helpers/cache';
import { useCacheData } from 'hooks/useCacheData';
import { useCurrentItemValue } from 'hooks/useCurrentItemValue';
import WordClassSelectModel, { IWordClassSelect } from 'models/WordClassSelectModel';
import React, { useContext, useMemo } from 'react';
import styled from 'styled-components';
import { SelectLayout } from './SelectLayout';

const KEY = 'wordClass';

type IProps = {
  isLinked?: boolean;
  onChange?: (wordClassId: number | null) => void;
  className?: string;
  disabled?: boolean;
  includeAllOption?: boolean;
  commonOnly?: boolean;
  finalOnly?: boolean;
};

const ALL_OPTION: IWordClassSelect = {
  id: null,
  name: 'All',
  hasGender: false,
  courseId: null,
};

export const WordClassSelect: React.FC<IProps> = (props) => {
  const { isLinked = true, onChange, includeAllOption = false, commonOnly = false, finalOnly = false } = props;

  const module = useContext(ModuleContext);
  const rawModuleValue = module.getItemValue(KEY);
  const moduleItemValue = rawModuleValue ?? null;

  const [currentWordClassId, setCurrentWordClassId] = useCurrentItemValue<number | null>(moduleItemValue, null, isLinked);

  const { courseId } = module;
  const cacheKey = getCacheKey(IRefIds.wordClass, courseId);
  const dataModel = useMemo(() => new WordClassSelectModel(courseId), [courseId]);
  const cacheSelectData = useCacheData(cacheKey, dataModel.get.bind(dataModel));

  const selectOptions = useMemo(() => {
    if (!cacheSelectData) {
      return null;
    }

    const data = cacheSelectData as IWordClassSelect[];

    let filteredOptions = commonOnly
      ? data.filter((option) => option.courseId == null)
      : data;

    // Filter to only show final word classes (those that don't inflect as something else)
    if (finalOnly) {
      filteredOptions = filteredOptions.filter((option) => !option.inflectAs);
    }

    const withoutAll = filteredOptions.filter((option) => option.id !== 0 && option.name.toLowerCase() !== 'other');

    if (includeAllOption) {
      return [ALL_OPTION, ...withoutAll];
    }

    return withoutAll;
  }, [cacheSelectData, commonOnly, finalOnly, includeAllOption]);

  const handleChange = (value: IWordClassSelect | null) => {
    const wordClassId = value?.id ?? null;
    setCurrentWordClassId(wordClassId);
    if (isLinked) {
      module.updateItemValue(KEY, wordClassId);
    }
    onChange && onChange(wordClassId);
  };

  const currentValueProp = currentWordClassId;
  const originalValueProp = isLinked ? moduleItemValue : undefined;

  return (
    <SelectLayoutStyled
      className={props.className}
      data={selectOptions}
      onChange={handleChange}
      label="Word Class"
      optionLabelKey="name"
      currentValue={currentValueProp}
      originalValue={originalValueProp}
      disabled={props.disabled}
    />
  );
};

const SelectLayoutStyled = styled(SelectLayout)`
  width: 175px;
  min-width: 175px;
  flex-grow: 0;
  flex-shrink: 0;
`;
